import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type {
  AdminReviewView,
  AdminReviewsPage,
  ReaderReviewView,
  ReviewCommentStatus,
  ReviewView,
} from '@/types/api';

/**
 * Payload for `POST /reviews`. Star ratings post live; supplying
 * `commentText` (non-empty) attaches a comment that lands in `'pending'`
 * until an admin approves. The server upserts on (readerId, bookId) so
 * re-submitting is safe.
 */
export interface CreateReviewPayload {
  readerId: string;
  bookId: string;
  rating: number;
  commentText?: string;
}

/**
 * Payload for `PATCH /reviews/:id`. Pass an empty string for
 * `commentText` to clear the comment (back to ratings-only); pass a
 * non-empty string to set/replace it (which re-enters moderation).
 * Omit to leave unchanged.
 */
export interface UpdateReviewPayload {
  rating?: number;
  commentText?: string;
}

export interface AdminListReviewsParams {
  status?: ReviewCommentStatus;
  bookId?: string;
  limit?: number;
  offset?: number;
}

export const reviewsService = {
  /**
   * Public per-book review list. Returns every approved comment plus
   * every ratings-only row; pending and rejected comments are scrubbed
   * for non-owners.
   */
  listForBook(bookId: string) {
    return apiRequest<ReviewView[]>(apiRoutes.reviews.forBook(bookId));
  },

  /**
   * Every review the given reader has left, newest first, enriched with the
   * book title + cover. Powers the Bookshelf "Reviews & ratings" section.
   * Account-scoped server-side.
   */
  listForReader(readerId: string) {
    return apiRequest<ReaderReviewView[]>(apiRoutes.reviews.forReader(readerId));
  },

  /**
   * The current reader's own review for a book, if any. Used to prefill
   * the form on the book detail panel.
   */
  findOwn(readerId: string, bookId: string) {
    return apiRequest<{ review: ReviewView | null }>(apiRoutes.reviews.mine, {
      query: { readerId, bookId },
    });
  },

  create(payload: CreateReviewPayload) {
    return apiRequest<ReviewView>(apiRoutes.reviews.create, {
      method: 'POST',
      body: payload,
    });
  },

  update(id: string, payload: UpdateReviewPayload) {
    return apiRequest<ReviewView>(apiRoutes.reviews.one(id), {
      method: 'PATCH',
      body: payload,
    });
  },

  remove(id: string) {
    return apiRequest<void>(apiRoutes.reviews.one(id), { method: 'DELETE' });
  },

  // -- Admin moderation --------------------------------------------------

  adminList(params: AdminListReviewsParams = {}) {
    return apiRequest<AdminReviewsPage>(apiRoutes.adminReviews.list, {
      query: {
        status: params.status,
        bookId: params.bookId,
        limit: params.limit,
        offset: params.offset,
      },
    });
  },

  adminModerate(id: string, commentStatus: 'approved' | 'rejected') {
    return apiRequest<AdminReviewView>(apiRoutes.adminReviews.one(id), {
      method: 'PATCH',
      body: { commentStatus },
    });
  },

  adminRemove(id: string) {
    return apiRequest<void>(apiRoutes.adminReviews.one(id), { method: 'DELETE' });
  },
};
